import React from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  RadioProps,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { useField } from "formik";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";

const useStyles = makeStyles({
  toggleButtonGroup: {
    "& .Mui-selected": {
      backgroundColor: "rgba(51, 122, 190, 1)",
      color: "white",
      "&:hover": {
        backgroundColor: "rgba(51, 122, 190, 1)",
      },
    },
    "& .MuiToggleButtonGroup-groupedHorizontal:not(:first-child)": {
      borderLeft: `1px solid rgba(51, 122, 190, 1)`,
    },
  },
  toggleButton: {
    color: "rgba(51, 122, 190, 1)",
    margin: "12px",
    minWidth: "160px",
    border: "1px solid rgba(51, 122, 190, 1)",
    "&.Mui-disabled": {
      color: "rgba(51, 122, 190, 0.4",
    },
  },
});

export type RadioOption = {
  label: string;
  value: any;
};

export type FormRadioProps = RadioProps & {
  name: string;
  label: string;
  options: RadioOption[];
  onChange?: (arg1: string) => any;
};

export default function FormRadioButton({
  name,
  label,
  options,
  disabled = false,
  onChange = null,
}: FormRadioProps): JSX.Element {
  const [field, meta, helpers] = useField(name);
  const errorText = meta.error && meta.touched ? meta.error : "";
  const hasError = !!errorText;

  const classes = useStyles();

  return (
    <FormControl component="fieldset" error={hasError}>
      <Typography style={{ fontWeight: 600, fontFamily: "OpenSans-SemiBold" }}>
        {label}
      </Typography>

      <ToggleButtonGroup
        value={field.value}
        exclusive
        onChange={(e, newSelection) => {
          if (field.value !== newSelection && Boolean(newSelection)) {
            helpers.setValue(newSelection);
          }
          if (onChange) {
            onChange(newSelection);
          }
          helpers.setTouched(true);
        }}
        className={classes.toggleButtonGroup}
      >
        {options.map((option: RadioOption) => (
          <ToggleButton
            key={option.value}
            value={option.value}
            className={classes.toggleButton}
            disabled={disabled}
          >
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {hasError && <FormHelperText>{errorText}</FormHelperText>}
    </FormControl>
  );
}

export function FormRadio({
  name,
  label,
  options,
  ...rest
}: FormRadioProps): JSX.Element {
  const [field, meta] = useField(name);
  const errorText = meta.error && meta.touched ? meta.error : "";
  const hasError = !!errorText;
  const id = `${name}-id`;

  return (
    <FormControl
      component="fieldset"
      error={hasError}
      margin={"dense"}
      style={{
        marginTop: "1rem",
      }}
    >
      <FormLabel>{label}</FormLabel>
      <RadioGroup
        {...field}
        style={{
          display: "flex",
          flexDirection: "row",
        }}
      >
        {options.map((option, index) => (
          <FormControlLabel
            key={index}
            value={option.value}
            control={<Radio />}
            label={option.label}
          />
        ))}
      </RadioGroup>
      {hasError && <FormHelperText>{meta.error}</FormHelperText>}
    </FormControl>
  );
}
